import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectTeacherTicketsTabSummary,
  tryGetTeacherTicketsTabSummary,
} from "../../store/slices/teacher-tickets";
import { Card, CardContent, Box, Divider, Chip, Container, Grid, Typography } from "@mui/material";
import CustomColumnMenu from "../layout/grid-column-menu";
import { DataGrid } from "@mui/x-data-grid";
import ModalMessages from "../layout/modal-messages";
import timeFromNow from "../../services/functions/timeFromNow";
import TeacherTicketsTabSummarySkeleton from "./skeleton";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const TeacherTabSummary = ({ businessId }) => {
  const dispatch = useDispatch();

  //Trae el detalle del usuario
  const teacherTicketsTabSummary = useSelector(selectTeacherTicketsTabSummary);

  //Detalle columnas
  const [rows, setRows] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    dispatch(tryGetTeacherTicketsTabSummary({ teacherId: businessId }));
  }, []);

  useEffect(() => {
    setRows(
      teacherTicketsTabSummary.data.map((x) => {
        return {
          ticketId: x.ticketId,
          nameOfSender: x.nameOfSender,
          titleOfTicket: x.titleOfTicket,
          topic: x.topic,
          subTopic: x.subTopic,
          statusOfMessage: x.statusOfMessage,
          timeDifference: x.timeDifference,
          statusIdOfTicket: x.statusIdOfTicket,
          statusOfMessageId: x.statusOfMessageId,
        };
      })
    );
  }, [teacherTicketsTabSummary]);

  //CUANDO SE INICIA LA GRILLA
  const initializeDataGrid = () => {
    return {
      ...rows,
      columns: {
        ...columns,
        columnVisibilityModel: {
          teacherCourseId: false,
          ticketId: false,
          statusIdOfTicket: false,
        },
      },
      pagination: { paginationModel: { pageSize: 5 } },
    };
  };

  const columns = [
    {
      field: "statusIdOfTicket",
      headerAlign: "center",
      align: "center",
      headerName: "statusIdOfTicket",
      flex: 1,
      headerClassName: "headRow",
    },
    {
      field: "ticketId",
      headerAlign: "center",
      align: "center",
      headerName: "ticketId",
      flex: 1,
      headerClassName: "headRow",
    },
    {
      field: "nameOfSender",
      headerAlign: "center",
      align: "center",
      headerName: "Remitente",
      flex: 1,
      headerClassName: "headRow",
    },
    {
      field: "titleOfTicket",
      headerAlign: "center",
      align: "center",
      headerName: "Título",
      flex: 1,
      headerClassName: "headRow",
    },
    {
      field: "topic",
      headerAlign: "center",
      align: "center",
      headerName: "Tópico",
      flex: 1,
      headerClassName: "headRow",
    },

    {
      field: "subTopic",
      headerAlign: "center",
      align: "center",
      headerName: "Sub Tópico",
      flex: 1,
      headerClassName: "headRow",
    },
    {
      field: "statusOfMessage",
      headerAlign: "center",
      align: "center",
      headerName: "Estado",
      flex: 1,
      headerClassName: "headRow",
      renderCell: (params) => {
        let chipColor;
        if (params.row.statusOfMessageId === 1) chipColor = "warning";
        if (params.row.statusOfMessageId === 2) chipColor = "success";
        if (params.row.statusOfMessageId === 3) chipColor = "default";

        return <Chip label={params.value} color={chipColor} size="small" />;
      },
    },
    {
      field: "timeDifference",
      headerAlign: "center",
      align: "center",
      headerName: "Enviado Hace",
      flex: 1,
      headerClassName: "headRow",
      renderCell: (params) => {
        return timeFromNow(params.value);
      },
    },
  ];

  return (
    <>
      <Card>
        <Box sx={{ px: 2 }}>
          <Grid container spacing={3} sx={{ pt: 3, pb: 3 }}>
            {teacherTicketsTabSummary.isLoading ? (
              <TeacherTicketsTabSummarySkeleton />
            ) : (
              <>
                <Grid item xs={6}>
                  <Typography
                    align="left"
                    style={{ fontWeight: "bold", fontSize: 18 }}
                  >
                    Últimas Comunicaciones
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  {isMobile ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {rows.map((row) => {
                        let chipColor = "default";
                        if (row.statusOfMessageId === 1) chipColor = "warning";
                        if (row.statusOfMessageId === 2) chipColor = "success";

                        return (
                          <Card key={row.ticketId} elevation={2} sx={{ border: "1px solid #eee" }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                {row.topic} - {row.subTopic}
                              </Typography>
                              <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5, mb: 1 }}>
                                {row.titleOfTicket}
                              </Typography>

                              <Typography variant="body2" color="textSecondary">
                                <strong>De:</strong> {row.nameOfSender}
                              </Typography>

                              <Divider sx={{ my: 1 }} />

                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Chip label={row.statusOfMessage} color={chipColor} size="small" />
                                <Typography variant="caption" color="textSecondary">
                                  {timeFromNow(row.timeDifference)}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )
                      })}
                      {rows.length === 0 && (
                        <Typography align="center" color="textSecondary">No hay comunicaciones.</Typography>
                      )}
                    </Box>
                  ) : (
                    <DataGrid
                      slots={{ columnMenu: CustomColumnMenu }}
                      autoHeight={true}
                      rowSelection={false}
                      getRowId={(row) => row.ticketId}
                      disableRowSelectionOnClick
                      rows={rows}
                      columns={columns}
                      pageSizeOptions={[5]}
                      initialState={initializeDataGrid()}
                      sx={{ "& .MuiDataGrid-virtualScroller": { minHeight: 250 } }}
                    />
                  )}
                </Grid>
              </>
            )}
          </Grid>
          {teacherTicketsTabSummary.errors &&
            teacherTicketsTabSummary.errors.length > 0 && (
              <ModalMessages errors={teacherTicketsTabSummary.errors} />
            )}
        </Box>
      </Card>
    </>
  );
};

export default TeacherTabSummary;
